'use client'

import { Helmet } from 'react-helmet-async'
import Header from './LandingPage/Layout/Header'
import Footer from './LandingPage/Layout/Footer'
import WhatsAppButton from './LandingPage/UI/WhatsAppButton'
import { DISPLAY_PHONE } from '../../config/contact'

export default function Terms() {
  return (
    <div className="bg-background-50">
      <Helmet>
        <title>Terms of Service | ScanMaadi</title>
        <meta name="description" content="Terms of Service for ScanMaadi — India's digital menu platform for restaurants and cafes. Read our terms governing the use of our services." />
      </Helmet>

      <Header />

      <main className="pt-28 pb-16 sm:pb-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-text-800">
              Terms of Service
            </h1>
            <p className="mt-4 text-text-500 text-sm">
              Effective Date: 1 January 2025 &nbsp;|&nbsp; Last Updated: 11 February 2026
            </p>
          </div>

          <div className="prose-custom space-y-8 text-text-600 leading-relaxed">
            {/* 1. Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">1. Introduction and Acceptance</h2>
              <p>
                Welcome to ScanMaadi ("<strong>Platform</strong>"), a digital menu management platform operated by
                Montivo Labs LLP ("<strong>Company</strong>", "<strong>we</strong>", "<strong>us</strong>", or "<strong>our</strong>"),
                a limited liability partnership registered under the Limited Liability Partnership Act, 2008,
                with its registered office at Bangalore, Karnataka, India.
              </p>
              <p className="mt-4">
                These Terms of Service ("<strong>Terms</strong>") constitute a legally binding agreement between you
                ("<strong>Vendor</strong>", "<strong>you</strong>", or "<strong>your</strong>") and the Company, governing
                your access to and use of the ScanMaadi platform, website (scanmaadi.com), mobile interfaces,
                and all related services (collectively, the "<strong>Service</strong>").
              </p>
              <p className="mt-4">
                By accessing, browsing, or using the Platform, or by registering an account, you acknowledge
                that you have read, understood, and agree to be bound by these Terms, our{' '}
                <a href="/privacy" className="text-primary-500 hover:text-accent-500 underline">Privacy Policy</a>, and our{' '}
                <a href="/refund-policy" className="text-primary-500 hover:text-accent-500 underline">Refund Policy</a>,
                which are incorporated herein by reference. If you do not agree to these Terms, you must not use the Platform.
              </p>
            </section>

            {/* 2. Definitions */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>"Platform"</strong> means the ScanMaadi website, application, and all associated digital tools and services.</li>
                <li><strong>"Vendor"</strong> means any individual, proprietor, partnership firm, company, or entity that registers on the Platform to create and manage digital menus.</li>
                <li><strong>"Customer"</strong> or <strong>"End User"</strong> means any person who scans a QR code and views a Vendor's digital menu.</li>
                <li><strong>"Subscription"</strong> means the annual digital menu service plan purchased by the Vendor.</li>
                <li><strong>"QR Products"</strong> means the physical QR code items offered by the Company, including acrylic QR stands and vinyl QR stickers.</li>
                <li><strong>"Menu Content"</strong> means all text, images, prices, descriptions, and other information uploaded or submitted by the Vendor to the Platform.</li>
                <li><strong>"AI Digitisation"</strong> means the Company's automated service for converting photographs of physical menus into structured digital menu data.</li>
              </ul>
            </section>

            {/* 3. Eligibility */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">3. Eligibility</h2>
              <p>To use the Platform, you represent and warrant that:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>You are at least 18 years of age and are competent to enter into a binding contract under the Indian Contract Act, 1872.</li>
                <li>You are the authorised owner or representative of the food establishment, restaurant, cafe, or eatery for which you are creating a digital menu.</li>
                <li>You possess all necessary licences and permits required to operate your food business in India, including but not limited to a valid FSSAI (Food Safety and Standards Authority of India) licence or registration, as applicable.</li>
                <li>You are not barred or restricted from using the Platform under any applicable law.</li>
                <li>All information you provide during registration is truthful, accurate, and complete.</li>
              </ul>
            </section>

            {/* 4. Account Registration & Security */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">4. Account Registration and Security</h2>
              <p>
                To access certain features of the Platform, you must create an account by providing accurate
                and complete information. You are responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Maintaining the confidentiality of your account credentials, including your password and any authentication tokens.</li>
                <li>All activities that occur under your account, whether or not authorised by you.</li>
                <li>Notifying us immediately at <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">hello@scanmaadi.in</a> if you become aware of any unauthorised use of your account or any other breach of security.</li>
                <li>Ensuring that only authorised personnel within your organisation access your account.</li>
              </ul>
              <p className="mt-4">
                The Company reserves the right to suspend or terminate your account at any time if we
                reasonably believe that your account information is inaccurate, or that your account
                has been compromised or is being used in violation of these Terms.
              </p>
            </section>

            {/* 5. Description of Services */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">5. Description of Services</h2>
              <p>ScanMaadi provides the following services to registered Vendors:</p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">5.1 Digital Menu Platform</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>AI Menu Digitisation:</strong> Automated conversion of physical menu photographs into structured, editable digital menus.</li>
                <li><strong>Unlimited Menu Updates:</strong> Vendors may update their menu items, prices, descriptions, and images at any time without additional charges during an active Subscription.</li>
                <li><strong>Searchable and Filterable Menus:</strong> End Users can search and filter menu items when viewing a Vendor's digital menu.</li>
                <li><strong>QR Code Generation:</strong> The Platform generates unique QR codes linked to the Vendor's digital menu for customer scanning.</li>
                <li><strong>Analytics Dashboard:</strong> Vendors can view scan statistics and analytics for their digital menus.</li>
                <li><strong>Priority Support:</strong> Access to customer support via WhatsApp and email during the Subscription period.</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">5.2 Physical QR Products</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>QR Stands:</strong> Premium acrylic table stands featuring the Vendor's unique QR code, available at ₹250 per unit.</li>
                <li><strong>QR Stickers:</strong> Durable vinyl stickers featuring the Vendor's unique QR code, available at ₹25 per unit.</li>
                <li>Delivery of physical QR Products is included at no additional shipping charge.</li>
              </ul>
            </section>

            {/* 6. Subscription and Pricing */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">6. Subscription and Pricing</h2>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">6.1 Subscription Plan</h3>
              <p>
                The Platform offers an annual Subscription at <strong>₹999 per year</strong> (Indian Rupees Nine Hundred
                and Ninety-Nine only). The Subscription includes access to all digital menu features described in
                Section 5.1 above.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">6.2 QR Product Quota</h3>
              <p>
                Each Subscription entitles the Vendor to purchase up to a combined total of <strong>5 (five)</strong> QR
                Products (stands and stickers combined). This is a shared quota — for example, a Vendor may purchase
                3 stands and 2 stickers, or 5 stickers and 0 stands, provided the total does not exceed 5 items.
                Vendors requiring more than 5 QR Products may contact us for custom multi-subscription arrangements.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">6.3 Subscription Period and Renewal</h3>
              <p>
                The Subscription period is 12 (twelve) months from the date of purchase. Upon expiry, the Vendor
                will be provided a grace period of 30 (thirty) days during which the Service will continue to remain
                accessible. After the grace period, if the Subscription is not renewed, the Service will be deactivated
                and the Vendor's digital menu will no longer be accessible to End Users via QR code scanning.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">6.4 Price Changes</h3>
              <p>
                The Company reserves the right to modify subscription fees and product prices at any time. Any
                price changes will not affect existing active Subscriptions but will apply upon renewal. We will
                provide reasonable notice of any material price changes via email or through the Platform.
              </p>
            </section>

            {/* 7. Payment Terms */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">7. Payment Terms</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>All payments on the Platform are processed exclusively through <strong>UPI (Unified Payments Interface)</strong>.</li>
                <li>Payments are processed through a third-party payment gateway. The Company does not store your payment credentials or UPI details.</li>
                <li>All prices displayed on the Platform are in <strong>Indian Rupees (INR)</strong> and are inclusive of all applicable taxes, including Goods and Services Tax (GST), unless stated otherwise.</li>
                <li>Payment must be completed in full before the Service or QR Products are activated or dispatched.</li>
                <li>You agree to pay all charges incurred by you or any user of your account at the prices in effect when such charges are incurred.</li>
                <li>In the event of a payment failure, the Company shall not be liable for any delay or non-delivery of the Service.</li>
              </ul>
            </section>

            {/* 8. Cancellation and Refunds */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">8. Cancellation and Refunds</h2>
              <p>
                Cancellation and refund terms are governed by our{' '}
                <a href="/refund-policy" className="text-primary-500 hover:text-accent-500 underline">Refund Policy</a>.
                The following general principles apply:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Subscriptions are billed annually and are non-refundable on a pro-rata basis for the unused portion of the Subscription period, except as required by applicable law.</li>
                <li>Physical QR Products, once manufactured and dispatched, are non-returnable due to their customised nature (each product is printed with the Vendor's unique QR code).</li>
                <li>Refunds, where applicable, will be processed to the original payment method within 7–10 business days.</li>
                <li>The Company reserves the right to refuse a refund if the Service has been substantially utilised or if the cancellation request is found to be fraudulent.</li>
              </ul>
            </section>

            {/* 9. Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">9. Intellectual Property</h2>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">9.1 Company's Intellectual Property</h3>
              <p>
                The Platform, including its design, source code, algorithms, user interface, graphics, logos,
                trademarks, service marks, trade names (including "ScanMaadi"), and all related intellectual
                property, are the exclusive property of Montivo Labs LLP or its licensors. These are protected
                under the Copyright Act, 1957, the Trade Marks Act, 1999, and other applicable intellectual
                property laws of India.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">9.2 Vendor's Content</h3>
              <p>
                You retain ownership of all Menu Content that you upload or submit to the Platform. By uploading
                Menu Content, you grant the Company a non-exclusive, worldwide, royalty-free licence to host,
                store, display, reproduce, and distribute your Menu Content solely for the purpose of operating
                and providing the Service, including displaying your menu to End Users who scan your QR codes.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">9.3 Feedback</h3>
              <p>
                Any suggestions, feedback, or ideas you provide to us regarding the Platform or Service may be
                used by us without any obligation to compensate you, and we shall be free to use such feedback
                for any purpose.
              </p>
            </section>

            {/* 10. Vendor Obligations */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">10. Vendor Obligations</h2>
              <p>As a Vendor using the Platform, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Provide accurate, up-to-date, and complete Menu Content, including correct item names, descriptions, prices, and allergen information where applicable.</li>
                <li>Comply with all applicable laws and regulations, including but not limited to the Food Safety and Standards Act, 2006 (FSSA), FSSAI regulations, the Consumer Protection Act, 2019, the Legal Metrology Act, 2009, and any local municipal regulations applicable to your food business.</li>
                <li>Display accurate pricing on your digital menu that matches the prices charged to customers at your establishment. Misleading pricing is prohibited.</li>
                <li>Ensure that all Menu Content, including images, does not infringe upon the intellectual property rights of any third party.</li>
                <li>Not use the Platform for any unlawful purpose, or to upload content that is defamatory, obscene, offensive, or in violation of any applicable law.</li>
                <li>Maintain valid and current FSSAI registration or licence as required under the Food Safety and Standards Act, 2006.</li>
                <li>Promptly update your digital menu to reflect any changes in your offerings, including the removal of unavailable items.</li>
              </ul>
            </section>

            {/* 11. Prohibited Conduct */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">11. Prohibited Conduct</h2>
              <p>You shall not, directly or indirectly:</p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Reverse engineer, decompile, disassemble, or otherwise attempt to derive the source code of the Platform.</li>
                <li>Use any automated means, including bots, scrapers, or crawlers, to access, collect data from, or interact with the Platform.</li>
                <li>Attempt to gain unauthorised access to the Platform, other user accounts, or any computer systems or networks connected to the Platform.</li>
                <li>Use the QR codes generated by the Platform for any purpose other than directing End Users to the Vendor's digital menu on the Platform.</li>
                <li>Modify, adapt, translate, or create derivative works based on the Platform or any part thereof.</li>
                <li>Remove, alter, or obscure any proprietary notices, labels, or marks on the Platform.</li>
                <li>Interfere with or disrupt the integrity or performance of the Platform or the data contained therein.</li>
                <li>Resell, sublicence, or commercially exploit the Platform or any part of the Service without the Company's prior written consent.</li>
                <li>Upload or transmit any viruses, malware, or other harmful code through the Platform.</li>
              </ul>
            </section>

            {/* 12. Data Privacy */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">12. Data Privacy and Protection</h2>
              <p>
                Your privacy is important to us. Our collection, use, storage, and disclosure of your personal
                information is governed by our{' '}
                <a href="/privacy" className="text-primary-500 hover:text-accent-500 underline">Privacy Policy</a>.
              </p>
              <p className="mt-4">
                The Company processes personal data in compliance with the Information Technology Act, 2000,
                the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal
                Data or Information) Rules, 2011, the Digital Personal Data Protection Act, 2023,
                and any other applicable data protection laws of India.
              </p>
              <p className="mt-4">
                By using the Platform, you consent to the collection and processing of your data as described in
                our Privacy Policy. The Platform's data is stored on secure cloud infrastructure (Amazon Web Services)
                and we implement industry-standard security measures to protect your information.
              </p>
              <p className="mt-4">
                With respect to End User data: the Company collects only anonymised scan analytics (such as scan
                counts and timestamps). No personal information of End Users is collected or stored by the Platform
                when they scan a Vendor's QR code and view the menu.
              </p>
            </section>

            {/* 13. Limitation of Liability */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">13. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by applicable law:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  The Platform and Service are provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis,
                  without warranties of any kind, whether express, implied, or statutory, including but not limited to
                  implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
                </li>
                <li>
                  The Company does not warrant that the Platform will be uninterrupted, error-free, secure, or free of
                  viruses or other harmful components.
                </li>
                <li>
                  The Company is not responsible for the accuracy, completeness, or reliability of any Menu Content
                  uploaded by Vendors. Vendors are solely responsible for ensuring that their menu information is correct
                  and compliant with applicable laws.
                </li>
                <li>
                  In no event shall the Company, its directors, partners, employees, or agents be liable for any indirect,
                  incidental, special, consequential, or punitive damages, including but not limited to loss of profits,
                  loss of data, loss of business, or loss of goodwill, arising out of or in connection with your use of
                  the Platform or Service.
                </li>
                <li>
                  The Company's total aggregate liability to you for all claims arising out of or relating to these Terms
                  or the Service shall not exceed the total Subscription fees paid by you to the Company in the 12 (twelve)
                  months immediately preceding the event giving rise to such liability.
                </li>
              </ul>
            </section>

            {/* 14. Indemnification */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">14. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless the Company, its partners, directors, employees,
                agents, and affiliates from and against any and all claims, liabilities, damages, losses, costs,
                and expenses (including reasonable legal fees) arising out of or in connection with:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Your use of the Platform or Service.</li>
                <li>Your breach of these Terms or any applicable law.</li>
                <li>Any Menu Content you upload, including claims of intellectual property infringement, defamation, or misleading information.</li>
                <li>Any claim by a third party (including End Users or regulatory authorities) arising from or related to your food establishment's operations, menu offerings, pricing, hygiene standards, or FSSAI compliance.</li>
                <li>Any violation of the Consumer Protection Act, 2019, or any consumer complaint arising from information displayed on your digital menu.</li>
              </ul>
            </section>

            {/* 15. Service Availability */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">15. Service Availability and Modifications</h2>
              <p>
                The Company strives to ensure the Platform is available at all times. However:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>We do not guarantee 100% uptime. The Platform may experience downtime due to scheduled maintenance, system upgrades, or unforeseen technical issues.</li>
                <li>We will make reasonable efforts to notify Vendors in advance of any scheduled maintenance that may affect Service availability.</li>
                <li>The Company reserves the right to modify, update, or discontinue any feature or aspect of the Service at any time, with or without notice. Material changes to core features will be communicated to active Vendors via email or through the Platform.</li>
                <li>We are not liable for any loss or inconvenience caused by temporary Service unavailability.</li>
              </ul>
            </section>

            {/* 16. Termination */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">16. Termination</h2>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">16.1 Termination by Vendor</h3>
              <p>
                You may discontinue your use of the Platform at any time by contacting us at{' '}
                <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">hello@scanmaadi.in</a>.
                Termination does not entitle you to a refund of any fees already paid, except as required by applicable law.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">16.2 Termination by the Company</h3>
              <p>
                The Company may suspend or terminate your account and access to the Service immediately, without
                prior notice, if:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>You breach any provision of these Terms.</li>
                <li>Your Menu Content is found to be unlawful, misleading, or in violation of applicable regulations.</li>
                <li>Your account is involved in fraudulent or suspicious activity.</li>
                <li>You fail to renew your Subscription after the grace period.</li>
                <li>Required by law or by order of a competent authority.</li>
              </ul>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">16.3 Effect of Termination</h3>
              <p>
                Upon termination of your account:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Your digital menu will be deactivated and will no longer be accessible to End Users.</li>
                <li>Your Menu Content will be retained on our servers for a period of 30 (thirty) days following termination, during which you may request a copy of your data.</li>
                <li>After the 30-day retention period, your Menu Content may be permanently deleted from our systems.</li>
                <li>Physical QR Products already delivered remain your property, though the QR codes printed on them will no longer function.</li>
                <li>Sections of these Terms that by their nature should survive termination shall continue to apply, including Sections 9 (Intellectual Property), 13 (Limitation of Liability), 14 (Indemnification), and 17 (Governing Law).</li>
              </ul>
            </section>

            {/* 17. Governing Law and Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">17. Governing Law and Dispute Resolution</h2>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">17.1 Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of India, without
                regard to its conflict of law principles.
              </p>

              <h3 className="text-lg font-semibold text-text-700 mt-6 mb-2">17.2 Dispute Resolution</h3>
              <p>
                In the event of any dispute, controversy, or claim arising out of or relating to these Terms
                or the Service:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>
                  <strong>Amicable Resolution:</strong> The parties shall first attempt to resolve the dispute
                  amicably through good-faith negotiations for a period of 30 (thirty) days from the date of
                  written notice of the dispute.
                </li>
                <li>
                  <strong>Mediation:</strong> If the dispute is not resolved through negotiations, the parties
                  may agree to submit the dispute to mediation in accordance with the Mediation Act, 2023.
                </li>
                <li>
                  <strong>Jurisdiction:</strong> If the dispute remains unresolved, it shall be subject to the
                  exclusive jurisdiction of the competent courts located in Bangalore, Karnataka, India.
                </li>
              </ul>
            </section>

            {/* 18. Force Majeure */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">18. Force Majeure</h2>
              <p>
                The Company shall not be liable for any failure or delay in performing its obligations under these
                Terms where such failure or delay results from circumstances beyond the Company's reasonable control,
                including but not limited to natural disasters, acts of God, epidemics or pandemics, war, terrorism,
                riots, civil unrest, government orders or regulations, strikes, lockouts, power outages,
                internet or telecommunications failures, cyber-attacks, or failure of third-party service providers.
              </p>
            </section>

            {/* 19. Changes to Terms */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">19. Changes to These Terms</h2>
              <p>
                The Company reserves the right to update, modify, or replace these Terms at any time at its sole
                discretion. Material changes will be communicated to registered Vendors via email or through a
                prominent notice on the Platform at least 15 (fifteen) days prior to the changes taking effect.
              </p>
              <p className="mt-4">
                Your continued use of the Platform after the effective date of revised Terms constitutes your
                acceptance of the changes. If you do not agree with the revised Terms, you must discontinue your
                use of the Platform.
              </p>
            </section>

            {/* 20. Severability */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">20. Severability</h2>
              <p>
                If any provision of these Terms is held to be invalid, illegal, or unenforceable by a court of
                competent jurisdiction, such provision shall be modified to the minimum extent necessary to make
                it valid and enforceable, or if modification is not possible, it shall be severed from these Terms.
                The remaining provisions shall continue in full force and effect.
              </p>
            </section>

            {/* 21. Entire Agreement */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">21. Entire Agreement</h2>
              <p>
                These Terms, together with the Privacy Policy and Refund Policy, constitute the entire agreement
                between you and the Company with respect to the subject matter hereof and supersede all prior
                or contemporaneous communications, representations, or agreements, whether oral or written, between
                the parties regarding the same.
              </p>
            </section>

            {/* 22. Waiver */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">22. Waiver</h2>
              <p>
                No failure or delay by the Company in exercising any right, power, or privilege under these Terms
                shall operate as a waiver thereof, nor shall any single or partial exercise of any such right,
                power, or privilege preclude any other or further exercise thereof.
              </p>
            </section>

            {/* 23. Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-text-800 mb-4">23. Contact Information</h2>
              <p>
                If you have any questions, concerns, or requests regarding these Terms of Service, please contact us:
              </p>
              <div className="mt-4 bg-background-100 rounded-xl p-6 border border-background-200">
                <p className="font-semibold text-text-800">Montivo Labs LLP</p>
                <p className="text-text-600 mt-1">(Operating as ScanMaadi)</p>
                <div className="mt-4 space-y-2 text-text-600">
                  <p>Registered Office: Bangalore, Karnataka, India</p>
                  <p>
                    Email:{' '}
                    <a href="mailto:hello@scanmaadi.in" className="text-primary-500 hover:text-accent-500 underline">
                      hello@scanmaadi.in
                    </a>
                  </p>
                  <p>Phone: {DISPLAY_PHONE}</p>
                </div>
              </div>
            </section>

            {/* Closing */}
            <section className="mt-12 pt-8 border-t border-background-200">
              <p className="text-text-500 text-sm">
                By using ScanMaadi, you acknowledge that you have read these Terms of Service in their entirety
                and agree to be bound by them.
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
      <WhatsAppButton />
    </div>
  )
}
